import { z } from "zod";

export const gamificationTierKeyWireSchema = z.enum([
  "listener",
  "supporter",
  "superfan",
  "force",
  "legend",
  "icon",
]);

const gamificationTierDisplayWireSchema = z.object({
  tier: gamificationTierKeyWireSchema,
  label: z.string(),
});

const gamificationScoreWireSchema = z.object({
  score: z.number(),
  decayedScore: z.number(),
  elo: z.number(),
  rawTier: gamificationTierDisplayWireSchema,
  benefitTier: gamificationTierDisplayWireSchema,
  monthsSinceActive: z.number(),
});

const gamificationArtistStatusWireSchema = gamificationScoreWireSchema.extend({
  artistId: z.string(),
  artistName: z.string(),
  artistImage: z.string().nullable(),
  streamCount: z.number(),
  streamTime: z.number(),
  accessesCount: z.number(),
  moneySpentStreaming: z.number(),
  moneySpentD2C: z.number(),
  level: z.number(),
  levelName: z.string(),
  nextLevelName: z.string().nullable(),
  levelProgress: z.number(),
  shadowColor: z.string(),
});

export const gamificationSummaryWireSchema = z.object({
  generatedAt: z.string(),
  platform: gamificationScoreWireSchema,
  gems: z.object({
    spendableBalance: z.number(),
    recentEarnedDelta: z.number(),
    recentEarnedWindowDays: z.number(),
  }),
  sourceMetrics: z.object({
    totalStreams: z.number(),
    totalStreamTime: z.number(),
    accesses: z.number(),
  }),
  previewArtistStatuses: z.array(gamificationArtistStatusWireSchema),
  artistStatuses: z.array(gamificationArtistStatusWireSchema),
});

export type GamificationSummaryWire = z.infer<
  typeof gamificationSummaryWireSchema
>;

export const artistScoreStatusWireSchema = gamificationScoreWireSchema.extend({
  scoreType: z.literal("artist"),
  rulebookVersion: z.literal("lis163-v1"),
  artistId: z.string(),
  label: z.string(),
  diagnostics: z.object({
    inputs: z.object({
      artist_stream_hours: z.number(),
      artist_money_earned_gbp: z.number(),
      experiences_organised: z.number(),
      gems_received: z.number(),
      unique_fans: z.number(),
      average_fan_score_adjustment: z.number(),
    }),
    sourceFacts: z.object({
      streamCount: z.number(),
      streamTime: z.number(),
      moneyEarnedGbp: z.number(),
      experiencesOrganised: z.number(),
      gemsReceived: z.number(),
      appliedGemDiscountMinorUnits: z.number(),
      uniqueFans: z.number(),
      contributorCount: z.number(),
    }),
    contributors: z.array(
      z.object({
        userId: z.string(),
        fanArtistElo: z.number(),
        fanArtistRawTier: z.string(),
        fanArtistScore: z.number(),
        fanArtistDecayedScore: z.number(),
      }),
    ),
  }),
});

export type ArtistScoreStatusWire = z.infer<typeof artistScoreStatusWireSchema>;

export const accessGemEconomicsStatusWireSchema = z.object({
  elo: z.number(),
  rawTier: gamificationTierDisplayWireSchema,
  benefitTier: gamificationTierDisplayWireSchema,
});

export const accessGemEconomicsQuoteWireSchema = z.object({
  eligible: z.boolean(),
  applied: z.boolean(),
  optedOut: z.boolean(),
  ineligibilityReason: z
    .enum([
      "no_purchase_price",
      "no_spendable_gems",
      "below_minimum_discount",
      "external_access",
      "engine_backed_access",
    ])
    .nullable(),
  currency: z.literal("GBP"),
  purchasePriceMinorUnits: z.number(),
  amountDueMinorUnits: z.number(),
  spendableGemsConsidered: z.number(),
  gemQuantity: z.number(),
  availableGemQuantity: z.number(),
  appliedDiscountMinorUnits: z.number(),
  availableDiscountMinorUnits: z.number(),
  rawAvailableValueMinorUnits: z.string(),
  grossGemValueMinorUnits: z.string(),
  roundingOverageMinorUnits: z.string(),
  valuePerGemMicroMinorUnits: z.string(),
  rulebookVersion: z.literal("lis163-v1"),
  platformStatus: accessGemEconomicsStatusWireSchema,
  artistStatus: accessGemEconomicsStatusWireSchema,
  premiumGateApplied: z.boolean(),
  conversionComponents: z.object({
    baseValueMinorUnitsPerGem: z.number(),
    platformTierMultiplier: z.string(),
    artistTierMultiplier: z.string(),
    onboardingMultiplier: z.string(),
    manualBoostMultiplier: z.string(),
    isPremium: z.boolean(),
  }),
  display: z.object({
    discount: z.string().nullable(),
    amountDue: z.string(),
    gemSpend: z.string().nullable(),
    rate: z.string(),
    status: z.string(),
  }),
});

export type AccessGemEconomicsQuoteWire = z.infer<
  typeof accessGemEconomicsQuoteWireSchema
>;
